/**
 * People's Chart Page - v4.0
 * Single "Yesterday" view with expandable rows showing 30-day trend
 * 
 * Changes from v3:
 * - Removed period dropdown (locked to "daily"/yesterday)
 * - A.Impact comes inline from chart API (no separate fetch)
 * - Click-to-expand rows with sparkline trend chart + summary
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { toast } from 'sonner';
import { 
  Music, TrendingUp, TrendingDown, Minus, Calendar, Search,
  ChevronDown, ChevronUp, BarChart3, Clock, Radio, 
  Lock, X, Star, Award
} from 'lucide-react';

// Period selector options (backend supports: daily, weekly, 2weeks, 3weeks, monthly)
const PERIOD_OPTIONS = [
  { key: 'daily',   label: 'Χθες' },
  { key: 'weekly',  label: 'Προηγ. Εβδομάδα (Δευ–Κυρ)' },
  { key: '2weeks',  label: '2 Εβδομάδες' },
  { key: '3weeks',  label: '3 Εβδομάδες' },
  { key: 'monthly', label: '4 Εβδομάδες' },
];
import { getBackendUrl } from '../utils/api';
import { useLanguage } from '../contexts/LanguageContext';
import PreviewPlayButton from '../components/shared/PreviewPlayButton';
import AudienceImpactBadge from '../components/AudienceImpactBadge';

const API = getBackendUrl();

// SEO
const SEO_CONFIG = {
  title: 'Radio Top Songs Greece | Real-Time Airplay Charts - Radio Solutions',
  description: 'Discover the most played songs on Greek radio stations. Real-time airplay data, listener retention metrics, and trending music analytics.',
  keywords: 'greek radio charts, radio airplay charts greece, top songs greek radio, airplay tracking, radio song rankings',
  ogUrl: 'https://radio-apps.online/radio-chart'
};

const decodeHtmlEntities = (text) => {
  if (!text || typeof text !== 'string') return text;
  const textarea = document.createElement('textarea');
  textarea.innerHTML = text;
  let decoded = textarea.value;
  return decoded.replace(/&apos;/g, "'").replace(/&#39;/g, "'").replace(/&quot;/g, '"').replace(/&amp;/g, '&');
};

// ─── Sparkline SVG ───
const Sparkline = ({ data, width = 200, height = 40 }) => {
  // data = array of { rank: number|null }
  // Lower rank = better (rank 1 = top)
  const ranked = data.filter(d => d.rank !== null);
  if (ranked.length < 2) return <span className="text-gray-500 text-xs italic">Ανεπαρκή δεδομένα</span>;

  const maxRank = Math.max(...ranked.map(d => d.rank), 20);
  const minRank = 1;
  const padding = 4;
  const usableW = width - padding * 2;
  const usableH = height - padding * 2;

  // Build points - map all data entries (including nulls for gaps)
  const points = [];
  let lastValid = null;
  data.forEach((d, i) => {
    const x = padding + (i / Math.max(data.length - 1, 1)) * usableW;
    if (d.rank !== null) {
      // Invert: rank 1 → top of chart, maxRank → bottom
      const y = padding + ((d.rank - minRank) / Math.max(maxRank - minRank, 1)) * usableH;
      points.push({ x, y, rank: d.rank });
      lastValid = { x, y, rank: d.rank };
    }
  });

  if (points.length < 2) return null;

  const pathD = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
  const lastPoint = points[points.length - 1];
  const firstPoint = points[0];
  const improved = lastPoint.rank < firstPoint.rank;
  const color = improved ? '#22c55e' : lastPoint.rank === firstPoint.rank ? '#a78bfa' : '#ef4444';

  return (
    <svg width={width} height={height} className="inline-block">
      <defs>
        <linearGradient id={`sg-${width}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.3" />
          <stop offset="100%" stopColor={color} stopOpacity="0.0" />
        </linearGradient>
      </defs>
      {/* Fill area */}
      <path
        d={`${pathD} L${lastPoint.x.toFixed(1)},${height} L${firstPoint.x.toFixed(1)},${height} Z`}
        fill={`url(#sg-${width})`}
      />
      {/* Line */}
      <path d={pathD} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      {/* Current position dot */}
      <circle cx={lastPoint.x} cy={lastPoint.y} r="3" fill={color} stroke="white" strokeWidth="1" />
    </svg>
  );
};

// ─── Trend Panel (expanded row) ───
const TrendPanel = ({ song, onClose }) => {
  const [historyData, setHistoryData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const token = localStorage.getItem('token');
        const params = new URLSearchParams({ days: '30' });
        if (song.normalized_key) params.set('normalized_key', song.normalized_key);
        else {
          params.set('artist', song.artist);
          params.set('title', song.title);
        }
        const resp = await fetch(`${API}/api/radio-chart/song/history?${params}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (resp.ok) {
          const data = await resp.json();
          setHistoryData(data);
        }
      } catch (err) {
        console.error('History fetch error:', err);
      }
      setLoading(false);
    };
    fetchHistory();
  }, [song]);

  const trend = historyData?.trend || {};
  const history = historyData?.history || [];
  const daysAvailable = historyData?.days_available || 0;

  const dirIcon = trend.direction === 'up' ? <TrendingUp className="w-4 h-4 text-green-400" /> :
                  trend.direction === 'down' ? <TrendingDown className="w-4 h-4 text-red-400" /> :
                  <Minus className="w-4 h-4 text-gray-400" />;

  return (
    <div className="bg-gradient-to-r from-purple-900/40 to-gray-900/60 border-t border-b border-purple-500/20 px-4 py-4" data-testid="trend-panel">
      {loading ? (
        <div className="flex items-center gap-2 text-gray-400 text-sm">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-400"></div>
          <span>Φόρτωση ιστορικού...</span>
        </div>
      ) : (
        <div className="flex flex-col sm:flex-row gap-4 items-start">
          {/* Sparkline */}
          <div className="flex-shrink-0">
            <div className="text-[10px] text-gray-500 mb-1 uppercase tracking-wider">Θέση 30 ημερών</div>
            {history.length > 1 ? (
              <div className="bg-black/30 rounded-lg p-2">
                <Sparkline data={history} width={220} height={50} />
                <div className="flex justify-between text-[9px] text-gray-600 mt-1 px-1">
                  <span>{history[0]?.date?.slice(5)}</span>
                  <span>{history[history.length - 1]?.date?.slice(5)}</span>
                </div>
              </div>
            ) : daysAvailable <= 1 ? (
              <div className="text-gray-500 text-xs">Μαζεύει δεδομένα...</div>
            ) : (
              <div className="text-gray-500 text-xs">Εκτός chart αυτή την περίοδο</div>
            )}
          </div>

          {/* Stats grid - only show with enough data */}
          <div className="flex-1 grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
            {/* Trend direction */}
            <div className="bg-white/5 rounded-lg p-2">
              <div className="text-[10px] text-gray-500 uppercase">Trend</div>
              <div className="flex items-center justify-center gap-1 mt-1">
                {dirIcon}
                <span className={`text-sm font-bold ${
                  trend.direction === 'up' ? 'text-green-400' :
                  trend.direction === 'down' ? 'text-red-400' : 'text-gray-300'
                }`}>
                  {trend.change_7d != null && trend.direction !== 'pending' ? (trend.change_7d > 0 ? `+${trend.change_7d}` : trend.change_7d) : '-'}
                </span>
              </div>
            </div>
            {/* Best rank */}
            <div className="bg-white/5 rounded-lg p-2">
              <div className="text-[10px] text-gray-500 uppercase">Best</div>
              <div className="text-sm font-bold text-yellow-400 mt-1">
                #{trend.best_rank || '-'}
              </div>
            </div>
            {/* Days in Top 20 */}
            <div className="bg-white/5 rounded-lg p-2">
              <div className="text-[10px] text-gray-500 uppercase">Top 20</div>
              <div className="text-sm font-bold text-purple-300 mt-1">
                {trend.days_in_top20 != null ? `${trend.days_in_top20}d` : '-'}
              </div>
              <div className="text-[9px] text-gray-500 mt-0.5">Ημέρες στο Top 20</div>
            </div>
            {/* Days in chart */}
            <div className="bg-white/5 rounded-lg p-2">
              <div className="text-[10px] text-gray-500 uppercase">Chart</div>
              <div className="text-sm font-bold text-gray-300 mt-1">
                {trend.days_in_chart != null ? `${trend.days_in_chart}d` : '-'}
              </div>
              <div className="text-[9px] text-gray-500 mt-0.5">Ημέρες στο Chart</div>
            </div>
          </div>

          {/* Summary text - only if we have meaningful data */}
          <div className="flex-shrink-0 sm:max-w-[200px]">
            {trend.summary_el && (
              <div className="bg-white/5 rounded-lg px-3 py-2">
                <div className="text-[10px] text-gray-500 uppercase mb-1">Σύνοψη</div>
                <p className="text-xs text-gray-200 leading-relaxed">{trend.summary_el}</p>
              </div>
            )}
            {song.station_names?.length > 0 && (
              <div className="mt-2 text-[10px] text-gray-500">
                <span className="uppercase tracking-wider">Σταθμοί: {song.station_names.length}</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};


// ─── Main Page ───
const RadioChartPage = ({ user }) => {
  const { t } = useLanguage();
  const [settings, setSettings] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedIdx, setExpandedIdx] = useState(null);
  const [apiError, setApiError] = useState(false);
  const [chartExportLoading, setChartExportLoading] = useState(false);
  const [period, setPeriod] = useState('daily');
  const [searchTerm, setSearchTerm] = useState('');

  const isSubscriber = user && (
    user.role === 'admin' || 
    user.subscription_status === 'active' ||
    user.assigned_stations?.length > 0
  );
  const isAdmin = user?.role === 'admin';

  // Fetch settings
  useEffect(() => {
    const controller = new AbortController();
    fetch(`${API}/api/radio-chart/settings`, { signal: controller.signal })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setSettings(d); })
      .catch(err => { if (err.name !== 'AbortError') console.error(err); });
    return () => controller.abort();
  }, []);

  // Fetch chart (always "daily")
  useEffect(() => {
    if (settings?.is_public || (settings && isAdmin)) {
      const controller = new AbortController();
      setLoading(true);
      setApiError(false);
      const token = localStorage.getItem('token');
      const limit = isSubscriber ? 300 : 20;
      fetch(`${API}/api/radio-chart/top?period=${period}&limit=${limit}&include_all=${isSubscriber === true}`, {
        headers: { Authorization: `Bearer ${token}` },
        signal: controller.signal
      })
        .then(r => r.ok ? r.json() : Promise.reject(r.status))
        .then(d => setChartData(d))
        .catch(err => { if (err !== 'AbortError') setApiError(true); })
        .finally(() => setLoading(false));
      return () => controller.abort();
    } else if (settings !== null) {
      setLoading(false);
    }
  }, [settings, isSubscriber, isAdmin, period]);

  const toggleExpand = (idx) => {
    setExpandedIdx(prev => prev === idx ? null : idx);
  };

  // Client-side filter by artist or title (search input)
  const visibleSongs = useMemo(() => {
    if (!chartData?.songs) return [];
    const s = (searchTerm || '').trim().toLowerCase();
    if (!s) return chartData.songs;
    return chartData.songs.filter(sg =>
      (sg.artist || '').toLowerCase().includes(s) ||
      (sg.title  || '').toLowerCase().includes(s)
    );
  }, [chartData, searchTerm]);

  // Draft mode
  if (settings && !settings.is_public && !isAdmin) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 flex items-center justify-center p-4">
        <div className="text-center max-w-lg">
          <div className="w-24 h-24 bg-purple-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <BarChart3 className="w-12 h-12 text-purple-400" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-4">The People's Chart</h1>
          <p className="text-gray-400 mb-4">{t('chartComingSoon')}</p>
          <p className="text-gray-500 text-sm">{t('chartUnderConstruction')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 py-8 px-4">
      <Helmet>
        <title>{SEO_CONFIG.title}</title>
        <meta name="description" content={SEO_CONFIG.description} />
        <meta name="keywords" content={SEO_CONFIG.keywords} />
        <meta property="og:title" content={SEO_CONFIG.title} />
        <meta property="og:description" content={SEO_CONFIG.description} />
        <meta property="og:url" content={SEO_CONFIG.ogUrl} />
        <meta property="og:type" content="website" />
        <link rel="canonical" href={SEO_CONFIG.ogUrl} />
      </Helmet>

      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8" data-testid="chart-header">
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-3 tracking-tight">
            The People's Chart
          </h1>
          <p className="text-gray-400 text-base max-w-xl mx-auto">
            {t('chartDescription') || 'Top 300 - Ranked by listener engagement across Greek radio stations'}
          </p>
          <p className="text-gray-600 text-xs mt-2">
            {t('clickSongForTrend') || 'Κάντε κλικ σε τραγούδι για 30-ημερο trend'}
          </p>
        </div>

        {/* Controls row — period + search */}
        <div className="flex flex-wrap items-center gap-3 mb-4" data-testid="chart-controls">
          <select
            data-testid="period-select"
            value={period}
            onChange={(e) => { setPeriod(e.target.value); setExpandedIdx(null); }}
            className="bg-white/5 text-white border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-purple-500"
          >
            {PERIOD_OPTIONS.map(p => (
              <option key={p.key} value={p.key} className="bg-gray-900">{p.label}</option>
            ))}
          </select>
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              data-testid="chart-search-input"
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Αναζήτηση καλλιτέχνη ή τίτλου…"
              className="w-full bg-white/5 text-white placeholder-gray-500 border border-white/10 rounded-lg pl-10 pr-3 py-2 text-sm focus:outline-none focus:border-purple-500"
            />
          </div>
        </div>

        {/* Chart */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
          </div>
        ) : chartData?.songs?.length > 0 ? (
          <div className="bg-white/5 backdrop-blur-sm rounded-xl overflow-hidden" data-testid="chart-table">
            {/* Stats bar */}
            <div className="bg-purple-600/20 px-4 py-3 border-b border-white/10">
              <div className="flex items-center justify-between text-sm text-gray-300">
                <span>{chartData.period_label || t('periodYesterday')}</span>
                <div className="flex items-center gap-4">
                  {isSubscriber && (
                    <button
                      data-testid="export-excel-btn"
                      disabled={chartExportLoading}
                      onClick={async () => {
                        if (chartExportLoading) return;
                        setChartExportLoading(true);
                        const authToken = localStorage.getItem('token');
                        
                        const attemptDownload = (retryCount = 0) => {
                          const xhr = new XMLHttpRequest();
                          xhr.open('GET', `${API}/api/radio-chart/export/excel?period=${period}`, true);
                          xhr.responseType = 'blob';
                          if (authToken) {
                            xhr.setRequestHeader('Authorization', `Bearer ${authToken}`);
                          }
                          
                          xhr.onload = function() {
                            if (xhr.status === 200) {
                              const blob = xhr.response;
                              const url = window.URL.createObjectURL(blob);
                              const a = document.createElement('a');
                              a.href = url;
                              a.download = `peoples_chart_${period}.xlsx`;
                              document.body.appendChild(a);
                              a.click();
                              document.body.removeChild(a);
                              window.URL.revokeObjectURL(url);
                              toast.success(t('excelDownloaded'));
                              setChartExportLoading(false);
                            } else if (xhr.status === 429 && retryCount < 2) {
                              const delay = (retryCount + 1) * 3000;
                              toast.info(`Επαναπροσπάθεια σε ${delay/1000}s...`);
                              setTimeout(() => attemptDownload(retryCount + 1), delay);
                            } else {
                              toast.error(t('excelDownloadError'));
                              setChartExportLoading(false);
                            }
                          };
                          
                          xhr.onerror = function() {
                            if (retryCount < 2) {
                              setTimeout(() => attemptDownload(retryCount + 1), 3000);
                            } else {
                              toast.error(t('excelDownloadError'));
                              setChartExportLoading(false);
                            }
                          };
                          
                          xhr.send();
                        };
                        
                        attemptDownload();
                      }}
                      className={`flex items-center gap-1 px-3 py-1.5 ${chartExportLoading ? 'bg-green-800 cursor-wait' : 'bg-green-600 hover:bg-green-700'} text-white rounded-lg text-xs font-medium transition-colors`}
                    >
                      {chartExportLoading ? (
                        <svg className="w-4 h-4 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                      ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                      )}
                      {chartExportLoading ? 'Εξαγωγή...' : 'Excel'}
                    </button>
                  )}
                  <span>{chartData.stations_count} {t('stationsCount')}</span>
                </div>
              </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="chart-songs-table">
                <thead className="bg-white/5 text-gray-400 text-xs uppercase">
                  <tr>
                    <th className="py-3 px-3 text-left w-10">#</th>
                    {/* v2.9.79 — Cover-art column. No header text (the
                        thumbnail speaks for itself) but the cell occupies
                        a fixed narrow width so the rest of the table
                        stays aligned. */}
                    <th className="py-3 px-2 text-left w-12" aria-label="Cover" />
                    <th className="py-3 px-3 text-left">{t('artistColumn')}</th>
                    <th className="py-3 px-3 text-left">{t('titleColumn')}</th>
                    <th className="py-3 px-3 text-center">Plays</th>
                    <th className="py-3 px-3 text-center hidden sm:table-cell">Retention</th>
                    <th className="py-3 px-3 text-center hidden sm:table-cell">Early Drop</th>
                    <th className="py-3 px-3 text-center">Score</th>
                    <th className="py-3 px-3 text-center hidden sm:table-cell">A.Impact</th>
                    <th className="py-3 px-3 text-center hidden md:table-cell">{t('stationsColumn')}</th>
                  </tr>
                </thead>
                <tbody className="text-white">
                  {visibleSongs.map((song, idx) => (
                    <React.Fragment key={song.normalized_key || idx}>
                      <tr
                        data-testid={`chart-row-${idx}`}
                        className={`border-t border-white/5 hover:bg-white/5 cursor-pointer transition-colors ${
                          expandedIdx === idx ? 'bg-purple-500/10' : ''
                        }`}
                        onClick={() => toggleExpand(idx)}
                      >
                        <td className="py-3 px-3">
                          <div className="flex items-center gap-1">
                            <span className={`font-bold ${
                              song.rank <= 3 ? 'text-yellow-400' :
                              song.rank <= 10 ? 'text-purple-400' : 'text-gray-400'
                            }`}>
                              {song.rank}
                            </span>
                            {expandedIdx === idx ? (
                              <ChevronUp className="w-3 h-3 text-purple-400" />
                            ) : (
                              <ChevronDown className="w-3 h-3 text-gray-600" />
                            )}
                          </div>
                        </td>
                        {/* v2.9.79 — Cover art thumb (from Shazam).
                            v2.9.82 — wrapped with PreviewPlayButton so
                            a 30s preview plays on click when iTunes /
                            Deezer returned a previewUrl. When no preview
                            is available the component degrades to a
                            plain thumbnail (no broken button). */}
                        <td className="py-3 px-2">
                          <PreviewPlayButton
                            src={song.cover_art_url}
                            previewUrl={song.preview_url}
                            trackKey={`chart-${song.normalized_key || idx}`}
                            artist={song.artist}
                            title={song.title}
                            size="sm"
                            testId={`chart-cover-${idx}`}
                          />
                        </td>
                        <td className="py-3 px-3 font-medium truncate max-w-[150px]">
                          {decodeHtmlEntities(song.artist)}
                        </td>
                        <td className="py-3 px-3 text-gray-400 truncate max-w-[150px]">
                          {decodeHtmlEntities(song.title)}
                        </td>
                        <td className="py-3 px-3 text-center font-bold text-purple-400">
                          {song.total_plays}
                        </td>
                        <td className="py-3 px-3 text-center hidden sm:table-cell text-green-400">
                          {song.avg_retention != null ? `${song.avg_retention}%` : '-'}
                        </td>
                        <td className="py-3 px-3 text-center hidden sm:table-cell text-orange-400">
                          {song.avg_early_drop != null ? `${song.avg_early_drop}%` : '-'}
                        </td>
                        <td className="py-3 px-3 text-center">
                          <span className="font-bold text-white bg-purple-600/30 px-2 py-1 rounded" data-testid={`score-${idx}`}>
                            {song.score || song.chart_score || song.people_chart_score || '-'}
                          </span>
                        </td>
                        <td className="py-3 px-3 text-center hidden sm:table-cell">
                          <AudienceImpactBadge 
                            value={song.audience_impact} 
                            level={song.impact_level || 'low'} 
                            compact 
                          />
                        </td>
                        <td className="py-3 px-3 text-center hidden md:table-cell text-gray-500">
                          {song.unique_station_count || song.station_count}
                        </td>
                      </tr>
                      {/* Expanded trend panel */}
                      {expandedIdx === idx && (
                        <tr>
                          <td colSpan="10" className="p-0">
                            <TrendPanel song={song} onClose={() => setExpandedIdx(null)} />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Subscription CTA */}
            {!isSubscriber && chartData.songs.length >= 20 && (
              <div className="bg-gradient-to-r from-purple-600/30 to-pink-600/30 border-t border-white/10">
                <div className="relative overflow-hidden">
                  <div className="blur-sm opacity-50 py-2">
                    <div className="text-center text-gray-400 text-sm py-2">21. - </div>
                    <div className="text-center text-gray-400 text-sm py-2">22. - </div>
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/80 to-transparent"></div>
                </div>
                <div className="px-6 py-8 text-center">
                  <div className="max-w-md mx-auto">
                    <Lock className="w-10 h-10 mx-auto mb-4 text-purple-400" />
                    <h3 className="text-xl font-bold text-white mb-2">{t('wantFullTop300')}</h3>
                    <p className="text-gray-400 text-sm mb-6">{t('getAccessDesc')}</p>
                    <div className="flex flex-col sm:flex-row gap-3 justify-center">
                      <a href="/pricing" className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-lg transition-colors">
                        {t('viewPackagesBtn')}
                      </a>
                      <a href="/login" className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white/10 hover:bg-white/20 text-white font-medium rounded-lg transition-colors">
                        {t('loginBtn')}
                      </a>
                    </div>
                    <p className="text-gray-500 text-xs mt-4">{t('freeTrial24hNew')}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : apiError ? (
          <div className="text-center py-20 text-gray-400" data-testid="chart-api-error">
            <Music className="w-16 h-16 mx-auto mb-4 opacity-30" />
            <p className="text-lg font-medium mb-2">Προσωρινό πρόβλημα σύνδεσης</p>
            <p className="text-sm mb-4">Δοκιμάστε ξανά σε λίγο</p>
            <button onClick={() => window.location.reload()} className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm transition-colors" data-testid="chart-retry-btn">
              Δοκιμάστε ξανά
            </button>
          </div>
        ) : (
          <div className="text-center py-20 text-gray-400" data-testid="chart-no-data">
            <Music className="w-16 h-16 mx-auto mb-4 opacity-30" />
            <p>{t('noDataForPeriod')}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default RadioChartPage;
